import { motion, useReducedMotion } from 'framer-motion';
import { Plane } from 'lucide-react';
import { formatCurrency, itemVariants, sectionVariants } from './constants';
import type { ResortFee } from './types';

/* ── Props ──────────────────────────────────────────────── */

interface FeesSectionProps {
    resortFees: ResortFee[];
    totalDailyFees: number;
}

/* ── Component ──────────────────────────────────────────── */

export default function FeesSection({ resortFees, totalDailyFees }: FeesSectionProps) {
    const shouldReduceMotion = useReducedMotion();

    /* Hide section if no fees and no total */
    if (!resortFees.length && totalDailyFees <= 0) return null;

    return (
        <section className="lux-space-md bg-[#fdfaf3]">
            <motion.div
                className="lux-space-px mx-auto max-w-6xl"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.15 }}
                variants={shouldReduceMotion ? undefined : sectionVariants}
            >
                {/* Kicker */}
                <motion.p className="lux-caps lux-caps-lg text-[#0e2727]/60" variants={shouldReduceMotion ? undefined : itemVariants}>
                    Additional Fees
                </motion.p>
                <hr className="mt-3 mb-8 w-20 border-t border-[#0e2727]/15" />

                {/* Fee rows */}
                <div className="max-w-2xl">
                    {resortFees.map((fee, index) => (
                        <motion.div
                            key={index}
                            className="flex items-baseline justify-between border-b border-[#0e2727]/10 py-3"
                            variants={shouldReduceMotion ? undefined : itemVariants}
                        >
                            <span className="lux-body lux-body-lg text-[#0e2727]/75">{fee.fee_label}</span>
                            <span className="lux-body lux-body-lg font-medium text-[#0e2727]">
                                {formatCurrency(fee.fee_amount, fee.currency)} / {fee.fee_frequency}
                            </span>
                        </motion.div>
                    ))}

                    {/* Total row */}
                    {totalDailyFees > 0 && (
                        <motion.div
                            className="mt-2 flex items-baseline justify-between border-t-2 border-[#0e2727]/20 pt-4"
                            variants={shouldReduceMotion ? undefined : itemVariants}
                        >
                            <span className="lux-body lux-body-lg font-semibold text-[#0e2727]">Total Daily Fees</span>
                            <span className="lux-body lux-body-lg font-semibold text-[#0e2727]">{formatCurrency(totalDailyFees)} / night</span>
                        </motion.div>
                    )}
                </div>

                {/* Disclaimer */}
                <motion.p className="lux-body lux-body-sm mt-6 text-[#0e2727]/60 italic" variants={shouldReduceMotion ? undefined : itemVariants}>
                    These fees are charged at the property and not included in the room rate.
                </motion.p>

                {/* Travelers CTA bar */}
                <motion.div
                    className="mt-8 flex flex-wrap items-center justify-between gap-3 rounded-sm border border-[#0e2727]/15 px-5 py-3"
                    variants={shouldReduceMotion ? undefined : itemVariants}
                >
                    <div className="flex items-center gap-3">
                        <Plane className="h-5 w-5 shrink-0 text-[#0e2727]/50" />
                        <p className="lux-body lux-body-sm text-[#0e2727]/75">Want access to benefits and non-public rates?</p>
                    </div>
                    <a
                        href="/clients"
                        className="lux-caps lux-caps-sm inline-flex h-10 items-center justify-center rounded-sm border border-[#0e2727]/40 px-6 text-[#0e2727] transition-all duration-500 hover:bg-[#0e2727] hover:text-[#F5F5F5]"
                    >
                        Contact an Agent
                    </a>
                </motion.div>
            </motion.div>
        </section>
    );
}
